/**
 * Fantasy Console 0 - New Cart Modal
 */

import { useState, useCallback, useEffect, useRef } from 'react'
import { useCart } from '../../context/CartContext'
import { useToast } from '../../context/ToastContext'
import { useAuth } from '../../context/AuthContext'
import styles from './Modal.module.css'

interface NewCartModalProps {
  isOpen: boolean
  onClose: () => void
}

export function NewCartModal({ isOpen, onClose }: NewCartModalProps) {
  const { createNewCart, setActiveTab } = useCart()
  const { success } = useToast()
  const { user, username } = useAuth()
  const [title, setTitle] = useState('')
  const [author, setAuthor] = useState('')
  const [description, setDescription] = useState('')
  const titleInputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (isOpen) {
      setTitle('')
      setAuthor('')
      setDescription('')
      setTimeout(() => titleInputRef.current?.focus(), 100)
    }
  }, [isOpen])

  const handleSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault()
      const cartTitle = title.trim() || 'Untitled Cart'
      createNewCart({
        title: cartTitle,
        author: user ? username || '' : author.trim(),
        description: description.trim(),
      })
      setActiveTab('code')
      success(`Created new cart: ${cartTitle}`)
      onClose()
    },
    [
      title,
      author,
      description,
      user,
      username,
      createNewCart,
      setActiveTab,
      success,
      onClose,
    ],
  )

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose()
      }
    },
    [onClose],
  )

  if (!isOpen) return null

  return (
    <div className='modal-overlay' onClick={onClose} onKeyDown={handleKeyDown}>
      <div className='modal-content' onClick={(e) => e.stopPropagation()}>
        <div className='modal-header'>
          <h2 className='modal-title'>New Cart</h2>
        </div>

        <form onSubmit={handleSubmit}>
          <div className='modal-body'>
            <div className='form-group'>
              <label className='form-label' htmlFor='cart-title'>
                Title <span className={styles.required}>*</span>
              </label>
              <input
                ref={titleInputRef}
                id='cart-title'
                type='text'
                className='form-input'
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder='My Awesome Game'
                maxLength={100}
              />
            </div>

            {!user && (
              <div className='form-group'>
                <label className='form-label' htmlFor='cart-author'>
                  Author
                </label>
                <input
                  id='cart-author'
                  type='text'
                  className='form-input'
                  value={author}
                  onChange={(e) => setAuthor(e.target.value)}
                  placeholder='Your name'
                  maxLength={100}
                />
              </div>
            )}

            <div className='form-group'>
              <label className='form-label' htmlFor='cart-description'>
                Description
              </label>
              <textarea
                id='cart-description'
                className='form-textarea'
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder='A short description of your game...'
                maxLength={500}
                rows={3}
              />
            </div>
          </div>

          <div className='modal-footer'>
            <button type='button' className='btn btn-ghost' onClick={onClose}>
              Cancel
            </button>
            <button type='submit' className='btn btn-primary'>
              Create Cart
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
